import {
    ClientApplication,
    ClientUser,
    ExtensionSignUpData,
    ForgotPasswordData,
    GoogleProfile,
    GoogleSignInProfile,
    SignInData,
    UpdaterLiteSignUpData,
} from '@numeo/types-core'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { setUserId as setAnalyticsUserId, setUserProperties as setAnalyticsUserProperties } from 'services/analytics'
import { amplitudeSquadronInstance, resetAnalyticsContext, setAnalyticsContext } from 'services/analytics/unified'
import { AUTH_TOKEN, decodeJwtPayload, getAuthToken, hasSessionOverride, stripEntityIdFromPath } from 'setup/SetupAxios'
import { updateNestedObject } from 'utils/update-nested-object'
import { AppDispatch } from '../../store'
import {
    DebugData,
    adminLoginAsToken,
    extensionSignup,
    forgotPassword as forgotPasswordAction,
    googleSignIn,
    googleSignup,
    login,
    logout as logoutAction,
    resetPassword as resetPasswordAction,
    selectApplication,
    selectOrganizationData,
    selectDebugData,
    selectIsAuthenticated,
    selectIsLoading,
    selectIsUserAdmin,
    selectUser,
    setApplication as setApplicationAction,
    setUser as setUserAction,
    updateDebugData as updateDebugDataAction,
    updaterLiteGoogleSignIn,
    updaterLiteGoogleSignup,
    updaterLiteSignup,
    verifyAuth,
    verifyResetCode as verifyResetCodeAction,
} from '../../store/authSlice'
import { preserveMembersWhenOmitted, sanitizeApplication, sanitizeUser } from './utils/user-sanitization'

interface VerifyResetCodeParams {
    email: string
    code: string
}

interface ResetPasswordParams {
    password: string
    email: string
    code: string
}

export const useAuthRedux = () => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    const user = useSelector(selectUser)
    const application = useSelector(selectApplication)
    const isAuthenticated = useSelector(selectIsAuthenticated)
    const isUserAdmin = useSelector(selectIsUserAdmin)
    const isLoading = useSelector(selectIsLoading)
    const debugData = useSelector(selectDebugData)
    const organizationData = useSelector(selectOrganizationData)

    // The EXTENSION_DATA / auth→extension sync that used to live here (and re-ran in all ~308 useAuth
    // consumers) now runs once per auth dispatch in store/middleware/extensionSyncMiddleware.ts.

    // Listen for logout events from the extension
    useEffect(() => {
        const handleAuthChanged = (event: CustomEvent) => {
            // If detail is null, it's a logout event from the extension. Only process if we're
            // currently authenticated to avoid circular events. Also skip while this tab is running
            // under its own company-switch override: the extension is pinned to the shared BASE
            // session and has no concept of per-tab overrides (see extensionSyncMiddleware.ts), so
            // a browser-wide "signed out" signal from it reflects the base identity ending, not
            // THIS tab's independent, still-valid override token — reacting to it here is exactly
            // the "switched tab gets logged out when a sibling tab's base session ends" bug.
            if (event.detail === null && isAuthenticated && !hasSessionOverride()) {
                // Use setTimeout to break out of the current execution context
                // This prevents the "store.getState() while reducer is executing" error
                setTimeout(() => {
                    dispatch(logoutAction(undefined))
                    navigate('/auth/sign-in')
                }, 0)
            }
        }

        window.addEventListener('numeo-auth-changed', handleAuthChanged as EventListener)

        return () => {
            window.removeEventListener('numeo-auth-changed', handleAuthChanged as EventListener)
        }
    }, [dispatch, navigate, isAuthenticated])

    // A DIFFERENT account logging in in another tab, OR the base session moving to a different
    // company, overwrites the shared base session in localStorage. Ordinary company switches never
    // touch localStorage (CompanySwitcher and AddCompanyModal write to sessionStorage only — see
    // SetupAxios.getAuthToken) — but the orgMerged flow (AcceptInvitationModal/Invite.tsx, when
    // accepting an invite hard-deletes the user's OLD company) deliberately DOES write the new
    // company's token to localStorage via setAuthToken, specifically so every open tab follows
    // along — same userId, different projectName. A same-account, same-company re-login (e.g.
    // session expired, user signs back in) also writes a new JWT string (different `iat`) but must
    // NOT force a reload. So: decode both tokens' `userId` AND `projectName` claims and reload when
    // either changed — covers the original "different account" case, the orgMerged broadcast, and a
    // fresh same-user login that lands on a different default company than what this tab shows.
    // Tabs with their own per-tab switch override are intentionally isolated and skip this — see
    // CompanySwitcher.handleSwitch.
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key !== AUTH_TOKEN || !e.newValue || !e.oldValue || !isAuthenticated) return
            // decodeJwtPayload (not a raw atob()) — JWTs are base64URL, and a plain atob() throws
            // on tokens containing `-`/`_`. A decode failure here means "can't confirm a genuinely
            // different identity", so it's treated the same as "no change" rather than reloading.
            const oldPayload = decodeJwtPayload(e.oldValue)
            const newPayload = decodeJwtPayload(e.newValue)
            const oldUserId = oldPayload?.userId
            const newUserId = newPayload?.userId
            if (!oldUserId || !newUserId) return

            const overrideToken = sessionStorage.getItem(AUTH_TOKEN)
            if (overrideToken) {
                // Tabs running their own per-tab company switch are normally isolated from base-
                // session changes (see CompanySwitcher.handleSwitch) — a different account logging
                // in as the new base, or the base moving to an unrelated company, doesn't affect an
                // unrelated override. The one exception: orgMerged (same account, base moves off a
                // hard-deleted company) can hard-delete the EXACT company this tab's override points
                // at, leaving it running against now-nonexistent data until its own JWT eventually
                // 401s. Detect that narrow case — same account, and this override's company is the
                // one just replaced — and force-reload only then.
                const overridePayload = decodeJwtPayload(overrideToken)
                const overrideNowDangling =
                    oldUserId === newUserId && overridePayload?.userId === oldUserId && overridePayload?.projectName === oldPayload?.projectName
                if (!overrideNowDangling) return
                window.location.replace(stripEntityIdFromPath())
                return
            }

            const identityChanged = oldUserId !== newUserId || oldPayload?.projectName !== newPayload?.projectName
            if (!identityChanged) return
            window.location.replace(stripEntityIdFromPath())
        }
        window.addEventListener('storage', handleStorageChange)
        return () => window.removeEventListener('storage', handleStorageChange)
    }, [isAuthenticated])

    // Keep the existing cross-surface Amplitude identity convention. Main-service and
    // the extension also identify users by email, so changing only admin would split funnels.
    useEffect(() => {
        const email = user?.email
        if (!email) return

        setAnalyticsUserId(email)

        if (amplitudeSquadronInstance) {
            setAnalyticsUserId(email, amplitudeSquadronInstance)
            // useAuth mounts once per consumer on the board. Send identity only when it changes.
            setAnalyticsUserProperties({ email }, amplitudeSquadronInstance, { onlyChanged: true })
        }
    }, [user?.email])

    const companyId = application?.id
    const mcNumber = application?.settings?.company?.mcNumber

    useEffect(() => {
        const projectName = application?.projectName
        if (!projectName) {
            resetAnalyticsContext()
            return
        }
        // Event-level context: stamped onto every trackUnifiedEvent payload (per-company attribution).
        // project_name included so action events carry it as an event property too (page views and
        // server events already do), not just as a user property that can lag a project switch.
        setAnalyticsContext({ company_id: companyId, mc_number: mcNumber, project_name: projectName, user_id: user?.id })
        if (!amplitudeSquadronInstance) return
        setAnalyticsUserProperties(
            {
                project_name: projectName,
                ...(companyId ? { company_id: companyId } : {}),
                ...(mcNumber ? { mc_number: mcNumber } : {}),
            },
            amplitudeSquadronInstance,
            { onlyChanged: true }
        )
    }, [application?.projectName, companyId, mcNumber, user?.id, user?.email])

    const setUser = (userValue: ClientUser | null | ((prev: ClientUser | null) => ClientUser | null)) => {
        if (typeof userValue === 'function') {
            const newValue = userValue(user)
            // Sanitize the user data to ensure userId is set properly
            const sanitizedValue = sanitizeUser(newValue)
            dispatch(setUserAction(sanitizedValue))
        } else {
            // Sanitize the user data to ensure userId is set properly
            const sanitizedValue = sanitizeUser(userValue)
            dispatch(setUserAction(sanitizedValue))
        }
    }

    const setApplication = (applicationValue: ClientApplication | null | ((prev: ClientApplication | null) => ClientApplication | null)) => {
        const nextValue = typeof applicationValue === 'function' ? applicationValue(application) : applicationValue
        const sanitizedValue = sanitizeApplication(preserveMembersWhenOmitted(nextValue, application), user)
        dispatch(setApplicationAction(sanitizedValue))
    }

    const updateDebugData = (data: DebugData) => {
        dispatch(updateDebugDataAction(data))
    }

    const loginWithRedux = async (formData: SignInData) => {
        const result = await dispatch(login(formData))

        // Check if the action was rejected and throw the error to be caught by the component
        if (login.rejected.match(result)) {
            throw result.payload
        }

        return result.payload
    }

    const logoutWithRedux = () => {
        // Read token + qrSessionId BEFORE dispatching logout (which clears the stored tokens)
        const token = getAuthToken()
        const qrSessionId = localStorage.getItem('qr_session_id')
        if (qrSessionId && token) {
            axios.post('/mobile-app/devices/unlink', { qrSessionId }, { headers: { Authorization: `Bearer ${token}` } }).catch((err) => {
                console.warn('[Logout] Failed to unlink device:', err?.response?.status ?? err?.message)
            })
        }
        dispatch(logoutAction(undefined))
        navigate('/auth/sign-in')
    }

    const forgotPasswordWithRedux = async (formData: ForgotPasswordData) => {
        const result = await dispatch(forgotPasswordAction(formData)).unwrap()
        return result
    }

    const verifyResetCodeWithRedux = async (params: VerifyResetCodeParams) => {
        const result = await dispatch(verifyResetCodeAction(params)).unwrap()
        return result
    }

    const resetPasswordWithRedux = async (params: ResetPasswordParams) => {
        const result = await dispatch(resetPasswordAction(params)).unwrap()
        return result
    }

    const verifyAuthWithRedux = async () => {
        await dispatch(verifyAuth()).unwrap()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateApplication = (keyPath: string, value: any) => {
        setApplication((prev) => {
            const updatedApplication = { ...prev }
            return updateNestedObject(updatedApplication, keyPath, value)
        })
    }

    const extensionSignupWithRedux = async (formData: ExtensionSignUpData) => {
        const result = await dispatch(extensionSignup(formData)).unwrap()
        return result
    }

    const updaterLiteSignupWithRedux = async (formData: UpdaterLiteSignUpData) => {
        const result = await dispatch(updaterLiteSignup(formData)).unwrap()
        return result
    }

    const updaterLiteGoogleSignupWithRedux = async (formData: GoogleProfile) => {
        const result = await dispatch(updaterLiteGoogleSignup(formData)).unwrap()
        return result
    }

    const updaterLiteGoogleSignInWithRedux = async (formData: GoogleSignInProfile) => {
        const result = await dispatch(updaterLiteGoogleSignIn(formData)).unwrap()
        return result
    }

    const googleSignupWithRedux = async (formData: GoogleProfile) => {
        const result = await dispatch(googleSignup(formData)).unwrap()
        return result
    }

    const googleSignInWithRedux = async (formData: GoogleSignInProfile) => {
        const result = await dispatch(googleSignIn(formData)).unwrap()
        return result
    }

    const adminLoginAsWithRedux = async (token: string) => {
        const result = await dispatch(adminLoginAsToken(token)).unwrap()
        return result
    }

    return {
        user,
        application,
        organizationData,
        debugData,
        updateDebugData,
        setUser,
        setApplication,
        verifyAuth: verifyAuthWithRedux,
        isAuthenticated,
        isLoading,
        isUserAdmin,
        updateApplication,
        login: loginWithRedux,
        logout: logoutWithRedux,
        forgotPassword: forgotPasswordWithRedux,
        verifyResetCode: verifyResetCodeWithRedux,
        resetPassword: resetPasswordWithRedux,
        extensionSignup: extensionSignupWithRedux,
        updaterLiteSignup: updaterLiteSignupWithRedux,
        updaterLiteGoogleSignup: updaterLiteGoogleSignupWithRedux,
        updaterLiteGoogleSignIn: updaterLiteGoogleSignInWithRedux,
        googleSignup: googleSignupWithRedux,
        googleSignIn: googleSignInWithRedux,
        adminLoginAs: adminLoginAsWithRedux,
    }
}
